/**
 * Claim bij openen (21-10-2026, Jordy): "wie een gesprek OPENT, heeft het tot hij
 * antwoordt of het gesprek verlaat."
 *
 * Bij het openen vraagt het chatvenster de claim (``claim: true``); de server geeft
 * het gesprek aan wie eerst komt (src/services/inbox_claim.py). Is het van een
 * ander, dan zegt de server door wie (``stand: 'other'``) en toont het venster dat.
 * Owners/managers bekijken zonder te claimen (``'view'``).
 *
 * Zolang het venster open is, gaat elke minuut een hartslag (zonder claim: na een
 * antwoord gelden de gewone regels). Verlaten (sluiten, ander gesprek, wegnavigeren,
 * tab dicht) geeft het gesprek meteen vrij. Vangnet: zonder hartslag vervalt de claim
 * na 3 minuten op de server; na 1 uur zonder enige handeling geeft het venster hem
 * zelf vrij. Het raakt de gelezen-status niet.
 */
import { useEffect, useState } from 'react'
import { getToken } from './api'

export const OPEN_VERVERS_MS = 60_000
/** 1 uur zonder toets, klik, scroll of aanraking: vrijgeven. */
export const INACTIEF_MS = 60 * 60_000

export interface InboxStand {
  /** 'mine' | 'other' | 'view' | 'none'; null = nog niet bekend. */
  stand: string | null
  /** Bij 'other': wie het gesprek heeft. */
  door: string | null
}

async function stuur(pad: string, body?: object, keepalive = false): Promise<any | null> {
  const token = getToken()
  if (!token) return null
  try {
    const r = await fetch(pad, {
      method: 'POST',
      credentials: 'same-origin',
      keepalive,
      headers: {
        Authorization: `Bearer ${token}`,
        ...(body ? { 'Content-Type': 'application/json' } : {}),
      },
      ...(body ? { body: JSON.stringify(body) } : {}),
    })
    return r.ok ? await r.json().catch(() => null) : null
  } catch {
    return null      // een gemiste hartslag is geen fout voor de gebruiker
  }
}

export function useInboxOpen(id: string | number | null | undefined): InboxStand {
  const [stand, setStand] = useState<InboxStand>({ stand: null, door: null })
  useEffect(() => {
    if (id == null || id === '') return
    const pad = `/api/admin/conversations/${id}`
    let weg = false
    const verwerk = (r: any) => {
      if (!weg && r && typeof r.stand === 'string') setStand({ stand: r.stand, door: r.door ?? null })
    }
    void stuur(`${pad}/inbox-open`, { claim: true }).then(verwerk)
    let laatsteActie = Date.now()
    const merk = () => { laatsteActie = Date.now() }
    const events = ['keydown', 'mousedown', 'scroll', 'touchstart'] as const
    events.forEach((e) => window.addEventListener(e, merk, { passive: true, capture: true }))
    let gesloten = false
    const sluit = () => {
      if (gesloten) return
      gesloten = true
      void stuur(`${pad}/inbox-close`, undefined, true)
    }
    const t = window.setInterval(() => {
      if (Date.now() - laatsteActie > INACTIEF_MS) { window.clearInterval(t); sluit(); return }
      void stuur(`${pad}/inbox-open`, { claim: false }).then(verwerk)
    }, OPEN_VERVERS_MS)
    window.addEventListener('pagehide', sluit)
    // Terug uit de terug/vooruit-cache: het venster staat weer open, dus opnieuw claimen.
    const terug = (e: PageTransitionEvent) => {
      if (!e.persisted) return
      gesloten = false
      laatsteActie = Date.now()
      void stuur(`${pad}/inbox-open`, { claim: true }).then(verwerk)
    }
    window.addEventListener('pageshow', terug)
    return () => {
      weg = true
      window.clearInterval(t)
      events.forEach((e) => window.removeEventListener(e, merk, { capture: true }))
      window.removeEventListener('pagehide', sluit)
      window.removeEventListener('pageshow', terug)
      sluit()
      setStand({ stand: null, door: null })
    }
  }, [id])
  return stand
}
